"use client";

import { useState, useEffect, useRef, ChangeEvent, FormEvent } from "react";
import { useRouter } from "next/navigation";
import { ArrowUp } from "lucide-react";
import { SuggestedQuestions } from "./SuggestedQuestions";
import dynamic from "next/dynamic";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSession } from "next-auth/react";
import Link from "next/link";

// Dynamic import Orb (official reactbits component) to avoid SSR issues with WebGL
const Orb = dynamic(() => import("@/components/Orb"), { ssr: false }) as React.ComponentType<{
  hue?: number; hoverIntensity?: number; rotateOnHover?: boolean;
  forceHoverState?: boolean; backgroundColor?: string;
}>;

const API_BASE = process.env.NEXT_PUBLIC_BASE_DATA_URI ?? "https://api.hoops.finance";

// ---------------------------------------------------------------------------
// My Chats dropdown — pixel icon (chat-bubble-solid from hackernoon/pixel-icon-library)
// ---------------------------------------------------------------------------

interface ChatSession {
  _id: string;
  title: string;
  lastActiveAt: string;
}

function MyChatsDropdown() {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: session } = useSession();
  const router = useRouter();
  const token = session?.user?.accessToken;

  useEffect(() => {
    if (!token || loaded) return;
    fetch(`${API_BASE}/ai/chat/sessions`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(10_000),
    })
      .then((r) => r.json() as Promise<{ sessions?: ChatSession[] }>)
      .then((d) => { setSessions(d.sessions ?? []); })
      .catch(() => { /* silent — landing shows empty on failure */ })
      .finally(() => { setLoaded(true); });
  }, [token, loaded]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, [open]);

  if (!token) return null;

  function formatDate(d: string) {
    const date = new Date(d);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    if (diff < 86400000) return "Today";
    if (diff < 172800000) return "Yesterday";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  return (
    <div ref={ref} className="relative z-20">
      <button
        onClick={() => { setOpen(!open); }}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] tracking-[0.04em] font-medium transition-colors hover:bg-[var(--surface)]"
        style={{ color: "var(--text-secondary)", border: "1px solid var(--border-subtle)" }}
        aria-label="My Chats"
      >
        {/* chat-bubble pixel icon */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21,3H3V4H2V5H1V15H2V16H3V17H7V18H8V19H9V20H10V21H11V18H12V17H21V16H22V15H23V5H22V4H21V3ZM21,15H10V16H9V17H8V16H7V15H3V5H21V15Z"/>
        </svg>
        My Chats
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 rounded-xl overflow-hidden"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border-default)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          }}
        >
          <div className="px-3 py-2.5" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <span className="text-[10px] tracking-[0.08em] uppercase font-medium" style={{ color: "var(--text-tertiary)" }}>
              Recent conversations
            </span>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {sessions.length === 0 ? (
              <p className="text-[11px] px-4 py-5 text-center" style={{ color: "var(--text-tertiary)" }}>
                No conversations yet
              </p>
            ) : (
              sessions.map((s) => (
                <button
                  key={s._id}
                  onClick={() => { setOpen(false); router.push(`/assistant/chat/${s._id}`); }}
                  className="w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-[var(--surface-hi)] transition-colors"
                >
                  <span className="text-[12px] truncate" style={{ color: "var(--text-primary)" }}>
                    {s.title}
                  </span>
                  <span className="text-[10px] flex-shrink-0" style={{ color: "var(--text-tertiary)" }}>
                    {formatDate(s.lastActiveAt)}
                  </span>
                </button>
              ))
            )}
          </div>

          <div className="px-3 py-2.5" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <button
              onClick={() => { setOpen(false); router.push("/assistant/chat"); }}
              className="w-full text-center text-[11px] font-medium py-1.5 rounded-md hover:bg-[var(--surface-hi)] transition-colors"
              style={{ color: "var(--accent)" }}
            >
              New conversation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AssistantLanding() {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [demoResponse, setDemoResponse] = useState<string | null>(null);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const q = query.trim();
    if (!q || isLoading) return;

    if (session?.user) {
      router.push(`/assistant/chat?q=${encodeURIComponent(q)}`);
      return;
    }

    if (demoResponse) {
      setShowAuthPrompt(true);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q }),
      });
      const data = (await res.json()) as { response?: string };

      if (res.status === 429) {
        setShowAuthPrompt(true);
        return;
      }

      setDemoResponse(data.response ?? null);
    } catch {
      setDemoResponse("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
      setQuery("");
    }
  }

  function handleSuggestion(prompt: string) {
    setQuery(prompt);
  }

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-6 overflow-hidden">
      {/* My Chats — top right */}
      <div className="absolute top-6 right-6 z-20">
        <MyChatsDropdown />
      </div>

      {/* Orb background */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
        <div style={{ width: 810, height: 810, position: "relative" }} className="pointer-events-auto">
          <Orb
            hue={142}
            hoverIntensity={0.2}
            rotateOnHover
            forceHoverState={false}
          />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[640px] w-full text-center">
        {/* Hero */}
        {!demoResponse && (
          <div className="mb-10 anim-fadeSlideInUp-500ms">
            <h1
              className="text-[2.5rem] sm:text-[3rem] font-normal leading-tight mb-3"
              style={{ fontFamily: "var(--font-instrument-serif), Georgia, serif", color: "var(--text-primary)" }}
            >
              Ask anything about{" "}
              <span style={{ color: "var(--accent)" }}>Stellar DeFi</span>
            </h1>
            <p className="text-[12px] tracking-[0.06em]" style={{ color: "var(--text-secondary)" }}>
              Powered by real-time data across every pool on the Stellar network
            </p>
          </div>
        )}

        {/* Demo response */}
        {demoResponse && (
          <div className="mb-8 text-left anim-fadeSlideInUp-300ms">
            <div className="rounded-xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }}>
              <p className="text-[10px] tracking-[0.1em] uppercase mb-3" style={{ color: "var(--text-secondary)" }}>
                Hoops Assistant
              </p>
              <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-primary)" }}>
                {demoResponse}
              </div>
            </div>
          </div>
        )}

        {/* Input */}
        <form
          onSubmit={(e) => { void handleSubmit(e); }}
          className="relative mb-5 anim-fadeSlideInUp-500ms"
          style={{ animationDelay: "0.2s" }}
        >
          <div
            className="relative rounded-full transition-all duration-200"
            style={{
              background: "var(--surface)",
              border: `1px solid ${inputFocused ? "var(--accent)" : "var(--border-subtle)"}`,
              boxShadow: inputFocused ? "0 0 0 2px var(--accent)" : "none",
            }}
          >
            <input
              type="text"
              value={query}
              onChange={(e: ChangeEvent<HTMLInputElement>) => { setQuery(e.target.value); }}
              onFocus={() => { setInputFocused(true); }}
              onBlur={() => { setInputFocused(false); }}
              placeholder={demoResponse ? "Sign in to continue..." : "What would you like to know?"}
              disabled={isLoading}
              className="w-full h-12 bg-transparent text-[13px] pl-5 pr-14 rounded-full outline-none"
              style={{ color: "var(--text-primary)" }}
            />
            <button
              type="submit"
              disabled={isLoading || !query.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 disabled:opacity-30"
              style={{ background: "var(--accent)", color: "var(--bg)" }}
              aria-label="Send"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </form>

        {/* Suggested chips */}
        {!demoResponse && (
          <div className="anim-fadeSlideInUp-500ms" style={{ animationDelay: "0.35s" }}>
            <SuggestedQuestions onSelect={handleSuggestion} />
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center gap-2 mt-6" style={{ color: "var(--text-secondary)" }}>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full animate-pulse"
                  style={{ background: "var(--accent)", animationDelay: `${i * 150}ms` }}
                />
              ))}
            </div>
            <span className="text-[11px] tracking-wide">Analyzing...</span>
          </div>
        )}
      </div>

      {/* Auth gate */}
      <Dialog open={showAuthPrompt} onOpenChange={setShowAuthPrompt}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Continue the conversation</DialogTitle>
            <DialogDescription>Sign in to ask follow-up questions and get personalized insights.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 pt-2">
            <Button className="w-full" style={{ background: "var(--accent)", color: "var(--bg)" }} onClick={() => { router.push("/auth"); }}>
              Sign In
            </Button>
            <p className="text-center text-sm" style={{ color: "var(--text-secondary)" }}>
              New here?{" "}
              <Link href="/auth" className="hover:underline" style={{ color: "var(--accent)" }}>Create an account</Link>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
